//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';
import {
  ContentLibrarySourcesGroups,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { getIsYouTubeCreatorTrackingEnabled } from '@influencers/selectors/getYouTubeCreatorSettings';
import { MediaLibrarySourcesButton } from '@media-library/components/top-bar/sources-dropdown/MediaLibrarySourcesButton';
import { MediaLibrarySourcesDropdownItem } from '@media-library/components/top-bar/sources-dropdown/MediaLibrarySourcesDropdownItem';
import {
  MediaLibrarySourcesConfigs,
  MediaLibrarySourcesDropdownItemData,
} from '@media-library/components/top-bar/sources-dropdown/mediaLibrarySourcesDropdownConstants';
import {
  computeSourcesSummary,
  hasSelectedDescendant,
  mapAllSources,
  mapDropdownStateToSourcesOptions,
  shouldBeSelected,
} from '@media-library/components/top-bar/sources-dropdown/mediaLibrarySourcesDropdownUtils';
import { AllTeamMembersModal } from '@media-library/components/top-bar/sources-modals/AllTeamMembersModal';
import { AllTrackedHashtagsModal } from '@media-library/components/top-bar/sources-modals/AllTrackedHashtagsModal';
import { AllTrackedUsersModal } from '@media-library/components/top-bar/sources-modals/AllTrackedUsersModal';
import { MediaLibraryTrackingIds } from '@media-library/constants/mediaLibraryTrackingIds';
import { createHasFeatureSelector } from '@redux/selectors/createHasFeatureSelector';
import { getUserId } from '@redux/selectors/getSessionInfo';
import { isDefined } from '@utils/filterPredicates';

const { ALL_INSTAGRAM, ALL_TRACKED_HASHTAGS, ALL_TRACKED_USERS, TEAM_MEDIA } =
  ContentLibrarySourcesGroups;

interface MediaLibrarySourcesDropdownProps extends MediaLibrarySourcesConfigs {
  handleChangeSources: (newSource: IContentLibrarySourcesOptions) => void;
  isDisabled?: boolean;
  selectedSources: IContentLibrarySourcesOptions;
}

export const MediaLibrarySourcesDropdown = ({
  handleChangeSources,
  instagramConfigs,
  isDisabled = false,
  pinterestUser,
  selectedSources,
  teamMembers,
}: MediaLibrarySourcesDropdownProps) => {
  const isInstagramCompetitorsEnabled = useSelector(
    createHasFeatureSelector(FeatureNames.InstagramCompetitors),
  );
  const isDamImportDropboxEnabled = useSelector(
    createHasFeatureSelector(FeatureNames.DamImportDropbox),
  );
  // TODO : PD-249761 - Remove TikTok Flag once ready for Production (GA)
  const isTikTokEnabled = useSelector(
    createHasFeatureSelector(FeatureNames.MediaLibraryTiktokFilter),
  );
  // TODO: PD-255285 Remove YoutubeCreatorTracking feature flag once released
  const isYouTubeEnabled = useSelector(getIsYouTubeCreatorTrackingEnabled);

  const isMultiHandleFeatureEnabled = useSelector(
    getHasMultiHandleInstagramEnabled,
  );
  const currentUserId = useSelector(getUserId);

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [openedModal, setOpenedModal] = useState<
    ContentLibrarySourcesGroups | undefined
  >(undefined);

  const allSources = mapAllSources({
    currentUserId,
    featureFlags: {
      isInstagramCompetitorsEnabled,
      isDamImportDropboxEnabled,
      isTikTokEnabled,
      isYouTubeEnabled,
      isMultiHandleFeatureEnabled,
    },
    selectedSources,
    sourcesConfigs: {
      instagramConfigs,
      pinterestUser,
      teamMembers,
    },
  });

  const sourcesLabel = computeSourcesSummary(allSources);

  const handleChangeSelectParent = (
    currentSource: MediaLibrarySourcesDropdownItemData,
    targetSource: MediaLibrarySourcesDropdownItemData,
  ) => {
    if (
      currentSource.paramKey === targetSource.paramKey &&
      currentSource.value === targetSource.value
    ) {
      currentSource.isIndeterminate = targetSource.isIndeterminate;
      currentSource.isSelected = targetSource.isSelected;
      currentSource.subItems = targetSource.subItems;
      return true;
    }

    if (isDefined(currentSource.subItems)) {
      for (const subItem of currentSource.subItems) {
        // Check if current item is parent of target item
        const isParentOfTarget = handleChangeSelectParent(
          subItem,
          targetSource,
        );

        // Update item's state only if it is parent of target
        if (isParentOfTarget) {
          // Set isIndeterminate to true if item has selected descendant
          currentSource.isIndeterminate = hasSelectedDescendant(
            currentSource.subItems,
          );

          // Set isSelected to true if all descendants are selected
          currentSource.isSelected = shouldBeSelected(currentSource);

          return true;
        }
      }
    }

    return false;
  };

  const handleChangeSelect = (
    targetSource: MediaLibrarySourcesDropdownItemData,
  ) => {
    handleChangeSelectParent(allSources, targetSource);

    const currentSourcesOptions = mapDropdownStateToSourcesOptions(allSources);
    handleChangeSources(currentSourcesOptions);
  };

  const handleChangeFilterText = (newFilterText: string) => {
    setSearchTerm(newFilterText);
  };

  const handleOpenModal = (sourcesGroup: ContentLibrarySourcesGroups) => {
    setIsDropdownOpen(false);
    setOpenedModal(sourcesGroup);
  };

  // Re-render sources modals when their open state changes.
  const getModalKey = (modal: ContentLibrarySourcesGroups) =>
    `${modal}-${openedModal === modal ? 'opened' : 'closed'}`;

  const teamMedia = allSources.subItems.find(
    ({ value }) => value === TEAM_MEDIA,
  );
  const allInstagram = allSources.subItems.find(
    ({ value }) => value === ALL_INSTAGRAM,
  );
  const trackedUsers = allInstagram?.subItems?.find(
    ({ value }) => value === ALL_TRACKED_USERS,
  );
  const trackedHashtags = allInstagram?.subItems?.find(
    ({ value }) => value === ALL_TRACKED_HASHTAGS,
  );

  return (
    <>
      <DropdownMenu
        onOpenChange={setIsDropdownOpen}
        open={!isDisabled && isDropdownOpen}
      >
        <DropdownMenu.Trigger asChild disabled={isDisabled}>
          <MediaLibrarySourcesButton
            label={sourcesLabel}
            onClick={() =>
              setIsDropdownOpen((isDropdownOpen) => !isDropdownOpen)
            }
          />
        </DropdownMenu.Trigger>

        <StyledDropdownContent
          align='start'
          onPointerDownOutside={() => setIsDropdownOpen(false)}
          sideOffset={5}
        >
          <DropdownContentWrapper>
            <SearchBar
              autoFocus={true}
              handleClear={() => handleChangeFilterText('')}
              onChange={handleChangeFilterText}
              placeholder={'Find sources...'}
              trackingIdPrefix={
                MediaLibraryTrackingIds.SOURCES_DROPDOWN_SEARCH_BAR
              }
              value={searchTerm}
              visuallyHiddenLabel={'Search options'}
            />

            <MediaLibrarySourcesDropdownItem
              handleChangeSelect={handleChangeSelect}
              handleOpenModal={handleOpenModal}
              searchTerm={searchTerm}
              source={allSources}
            />
          </DropdownContentWrapper>
        </StyledDropdownContent>
      </DropdownMenu>

      {!isDisabled && isDefined(teamMedia) && (
        <AllTeamMembersModal
          key={getModalKey(TEAM_MEDIA)}
          handleChangeSelects={handleChangeSelect}
          handleCloseModal={() => setOpenedModal(undefined)}
          isOpen={openedModal === TEAM_MEDIA}
          teamMedia={teamMedia}
        />
      )}

      {!isDisabled && isDefined(trackedUsers) && (
        <AllTrackedUsersModal
          key={getModalKey(ALL_TRACKED_USERS)}
          handleChangeSelects={handleChangeSelect}
          handleCloseModal={() => setOpenedModal(undefined)}
          isOpen={openedModal === ALL_TRACKED_USERS}
          trackedUsers={trackedUsers}
        />
      )}

      {!isDisabled && isDefined(trackedHashtags) && (
        <AllTrackedHashtagsModal
          key={getModalKey(ALL_TRACKED_HASHTAGS)}
          handleChangeSelects={handleChangeSelect}
          handleCloseModal={() => setOpenedModal(undefined)}
          isOpen={openedModal === ALL_TRACKED_HASHTAGS}
          trackedHashtags={trackedHashtags}
        />
      )}
    </>
  );
};

const StyledDropdownContent = styled(DropdownMenu.Content)(
  ({ theme }) => `
  height: ${theme.wideMeasure};
  min-width: 300px;
  overflow-y: auto;
  padding: ${theme.spacings[3]};
`,
);

const DropdownContentWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[3]};
`,
);
